import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Card,
  CardContent,
  Stack,
  Avatar,
  alpha,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { FilterAltOff as ClearFilterIcon } from '@mui/icons-material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Inventory as InventoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocalOffer as LocalOfferIcon,
  EventAvailable as EventAvailableIcon,
  Refresh as RefreshIcon,
    Print as PrintIcon,
    FileDownload as ExcelIcon,
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { useLanguage } from '../../contexts/LanguageContext';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import config from '../../config';
import { itemsService } from '../../services/items.service';
import { Item } from '../../types/items.types';
import ItemDialog from '../../components/items/ItemDialog';
import { useConfirm } from '../../hooks/useConfirm';
import { toast } from 'react-toastify';

const translations = {
  en: {
    title: 'Items Management',
    addItem: 'Add Item',
    search: 'Search items...',
    itemCode: 'Item Code',
    itemName: 'Item Name',
    category: 'Category',
    baseUnit: 'Base Unit',
    itemType: 'Type',
    status: 'Status',
    hasExpiry: 'Tracks Expiry',
    actions: 'Actions',
    active: 'Active',
    inactive: 'Inactive',
    noItems: 'No items found',
    loading: 'Loading items...',
    error: 'Failed to load items',
    deleteConfirm: 'Are you sure you want to delete this item?',
    product: 'Product',
    service: 'Service',
    rawMaterial: 'Raw Material',
    totalItems: 'Total Items',
    activeItems: 'Active Items',
    withExpiry: 'Track Expiry',
    yes: 'Yes',
    no: 'No',
    allCategories: 'All Categories',
    allTypes: 'All Types',
    allStatus: 'All Status',
    clearFilters: 'Clear Filters',
  },
  ar: {
    title: 'إدارة الأصناف',
    addItem: 'إضافة صنف',
    search: 'بحث عن الأصناف...',
    itemCode: 'كود الصنف',
    itemName: 'اسم الصنف',
    category: 'التصنيف',
    baseUnit: 'الوحدة الأساسية',
    itemType: 'النوع',
    status: 'الحالة',
    hasExpiry: 'يتتبع الانتهاء',
    actions: 'الإجراءات',
    active: 'نشط',
    inactive: 'غير نشط',
    noItems: 'لا توجد أصناف',
    loading: 'جاري تحميل الأصناف...',
    error: 'فشل تحميل الأصناف',
    deleteConfirm: 'هل أنت متأكد من حذف هذا الصنف؟',
    product: 'منتج',
    service: 'خدمة',
    rawMaterial: 'مادة خام',
    totalItems: 'إجمالي الأصناف',
    activeItems: 'الأصناف النشطة',
    withExpiry: 'تتبع الانتهاء',
    yes: 'نعم',
    no: 'لا',
    allCategories: 'جميع التصنيفات',
    allTypes: 'جميع الأنواع',
    allStatus: 'جميع الحالات',
    clearFilters: 'مسح الفلاتر',
  },
};

const ItemsPage: React.FC = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  const [items, setItems] = useState<Item[]>([]);
  const [filteredItems, setFilteredItems] = useState<Item[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const hasActiveFilters = searchQuery || filterCategory || filterType || filterStatus;

  const categories = useMemo(() => {
    const cats = new Map<number, string>();
    items.forEach(i => { if (i.categoryId) cats.set(i.categoryId, isRTL ? (i.categoryNameAr || i.categoryName) : i.categoryName); });
    return Array.from(cats.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items, isRTL]);

  const itemTypes = useMemo(() => Array.from(new Set(items.map(i => i.itemType).filter(Boolean))), [items]);

  useEffect(() => {
    loadItems();
  }, []);

  useEffect(() => {
    let result = items;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        i.itemCode.toLowerCase().includes(q) ||
        i.itemName.toLowerCase().includes(q) ||
        i.itemNameAr.toLowerCase().includes(q)
      );
    }
    if (filterCategory) result = result.filter(i => String(i.categoryId) === filterCategory);
    if (filterType) result = result.filter(i => i.itemType === filterType);
    if (filterStatus === 'active') result = result.filter(i => i.isActive);
    if (filterStatus === 'inactive') result = result.filter(i => !i.isActive);
    setFilteredItems(result);
    setPage(0);
  }, [searchQuery, filterCategory, filterType, filterStatus, items]);

  const paginatedItems = useMemo(
    () => filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredItems, page, rowsPerPage]
  );

  const loadItems = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await itemsService.getAll(true);
      setItems(data);
      setFilteredItems(data);
    } catch (err) {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: t.deleteConfirm, severity: 'error' });
    if (!ok) return;
    try {
      await itemsService.delete(id);
      loadItems();
    } catch (err) {
      toast.error(t.error);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleDialogSave = () => {
    loadItems();
  };

  const handleAddClick = () => {
    setEditingItem(null);
    setDialogOpen(true);
  };

  const handleEditClick = (item: Item) => {
    setEditingItem(item);
    setDialogOpen(true);
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      // Company Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          worksheet.addImage(logoImage, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 60 } });
        } catch {}
      }

      // Company Header
      worksheet.mergeCells('A1:E1');
      const companyCell = worksheet.getCell('A1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 16, bold: true, color: { argb: 'FF5E35B1' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 50;

      // Report Title
      worksheet.mergeCells('A3:E3');
      const titleCell = worksheet.getCell('A3');
      titleCell.value = t.title;
      titleCell.font = { size: 13, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5E35B1' } };
      worksheet.getRow(3).height = 28;

      // Date row
      worksheet.mergeCells('A4:E4');
      const dateCell = worksheet.getCell('A4');
      dateCell.value = `${isRTL ? 'تاريخ التصدير' : 'Exported'}: ${new Date().toLocaleDateString('en-GB')} | ${isRTL ? 'الإجمالي' : 'Total'}: ${filteredItems.length}`;
      dateCell.font = { size: 9, color: { argb: 'FF666666' } };
      dateCell.alignment = { horizontal: 'center' };

      // Headers row 6
      const headers = [t.itemCode, t.itemName, t.itemType, t.hasExpiry, t.status];
      const headerRow = worksheet.getRow(6);
      headerRow.values = headers;
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 24;

      // Data rows
      let currentRow = 7;
      filteredItems.forEach((item, idx) => {
        const row = worksheet.getRow(currentRow);
        row.values = [
          item.itemCode,
          isRTL ? item.itemNameAr : item.itemName,
          getItemTypeName(item.itemType),
          item.hasExpiryDate ? t.yes : t.no,
          item.isActive ? t.active : t.inactive,
        ];
        row.alignment = { horizontal: 'center', vertical: 'middle' };
        row.height = 20;
        if (idx % 2 === 0) {
          row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
        }
        currentRow++;
      });

      // Column widths
      worksheet.getColumn(1).width = 14;
      worksheet.getColumn(2).width = 35;
      worksheet.getColumn(3).width = 16;
      worksheet.getColumn(4).width = 16;
      worksheet.getColumn(5).width = 14;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Items_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Excel exported successfully');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(isRTL ? 'فشل التصدير' : 'Excel export failed');
    }
  };

  const handlePrintList = () => {
    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:#1976d2;margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const rows = filteredItems.map(item => `
      <tr>
        <td>${item.itemCode}</td>
        <td>${isRTL ? item.itemNameAr : item.itemName}</td>
        <td>${getItemTypeName(item.itemType)}</td>
        <td>${item.hasExpiryDate ? (isRTL ? 'نعم' : 'Yes') : (isRTL ? 'لا' : 'No')}</td>
        <td>${item.isActive ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'غير نشط' : 'Inactive')}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head>
      <style>
        body{font-family:${isRTL ? 'Cairo,' : ''}Arial,sans-serif;margin:20px;font-size:12px;color:#333;}
        .header{text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;}
        .header h2{color:#1976d2;margin:5px 0;}
        .header p{font-size:11px;color:#666;margin:0;}
        h1{text-align:center;color:#1976d2;margin:10px 0 15px;font-size:18px;}
        table{width:100%;border-collapse:collapse;margin-top:10px;}
        th,td{border:1px solid #ddd;padding:6px 8px;font-size:11px;}
        th{background:#1976d2;color:white;font-weight:bold;}
        tr:nth-child(even){background:#f9f9f9;}
        @page{size:A4 landscape;margin:1cm;}
      </style></head><body>
      ${companyHeader}
      <h1>${t.title}</h1>
      <table>
        <thead><tr>
          <th>${t.itemCode}</th>
          <th>${t.itemName}</th>
          <th>${t.itemType}</th>
          <th>${t.hasExpiry}</th>
          <th>${t.status}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="margin-top:10px;font-size:11px;color:#999;">Printed: ${new Date().toLocaleString()}</p>
    </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
  };

  const getItemTypeName = (type: string) => {
    switch (type) {
      case 'Product': return t.product;
      case 'Service': return t.service;
      case 'RawMaterial': return t.rawMaterial;
      default: return '';
    }
  };

  const calculateStats = () => {
    const total = items.length;
    const active = items.filter(i => i.isActive).length;
    const withExpiry = items.filter(i => i.hasExpiryDate).length;
    return { total, active, withExpiry };
  };

  const stats = calculateStats();

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 56,
              height: 56,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <InventoryIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {t.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.total} {language === 'ar' ? 'صنف' : 'items'}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={isRTL ? 'تصدير Excel' : 'Export Excel'}>
            <IconButton onClick={exportToExcel} color="success"><ExcelIcon /></IconButton>
          </Tooltip>
          <Tooltip title={isRTL ? 'طباعة القائمة' : 'Print List'}>
            <IconButton onClick={handlePrintList} color="primary"><PrintIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={loadItems} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddClick}
            size="large"
            sx={{
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
              px: 4,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(45deg, #764ba2 30%, #667eea 90%)',
              },
            }}
          >
            {t.addItem}
          </Button>
        </Stack>
      </Stack>

      {/* Stats Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <Card
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {t.totalItems}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {stats.total}
                </Typography>
              </Box>
              <InventoryIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {t.activeItems}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {stats.active}
                </Typography>
              </Box>
              <CheckCircleIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>

        <Card
          sx={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {t.withExpiry}
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {stats.withExpiry}
                </Typography>
              </Box>
              <EventAvailableIcon sx={{ fontSize: 50, opacity: 0.3 }} />
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* Search & Table */}
      <Paper
        elevation={4}
        sx={{
          p: 3,
          borderRadius: 3,
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }} alignItems="center">
          <TextField
            placeholder={t.search}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search color="primary" /></InputAdornment>,
            }}
            sx={{ flex: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t.category}</InputLabel>
            <Select value={filterCategory} label={t.category} onChange={(e) => setFilterCategory(e.target.value)}>
              <MenuItem value="">{t.allCategories}</MenuItem>
              {categories.map(([id, name]) => <MenuItem key={id} value={String(id)}>{name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{t.itemType}</InputLabel>
            <Select value={filterType} label={t.itemType} onChange={(e) => setFilterType(e.target.value)}>
              <MenuItem value="">{t.allTypes}</MenuItem>
              {itemTypes.map(type => <MenuItem key={type} value={type}>{getItemTypeName(type)}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>{t.status}</InputLabel>
            <Select value={filterStatus} label={t.status} onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="">{t.allStatus}</MenuItem>
              <MenuItem value="active">{t.active}</MenuItem>
              <MenuItem value="inactive">{t.inactive}</MenuItem>
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button size="small" startIcon={<ClearFilterIcon />} onClick={() => { setSearchQuery(''); setFilterCategory(''); setFilterType(''); setFilterStatus(''); }}>
              {t.clearFilters}
            </Button>
          )}
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : filteredItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <InventoryIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              {t.noItems}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    background: alpha(theme.palette.primary.main, 0.1),
                  }}
                >
                  <TableCell sx={{ fontWeight: 700 }}>{t.itemCode}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t.itemName}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t.itemType}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t.hasExpiry}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t.status}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">{t.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedItems.map((item) => (
                    <TableRow
                      key={item.itemId}
                      hover
                      sx={{
                        '&:hover': {
                          background: alpha(theme.palette.primary.main, 0.05),
                        },
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={item.itemCode}
                          size="small"
                          icon={<LocalOfferIcon />}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {isRTL ? item.itemNameAr : item.itemName}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getItemTypeName(item.itemType)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.hasExpiryDate ? t.yes : t.no}
                          size="small"
                          color={item.hasExpiryDate ? 'warning' : 'default'}
                          icon={item.hasExpiryDate ? <EventAvailableIcon /> : undefined}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.isActive ? t.active : t.inactive}
                          color={item.isActive ? 'success' : 'default'}
                          size="small"
                          icon={item.isActive ? <CheckCircleIcon /> : <CancelIcon />}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleEditClick(item)}
                          sx={{
                            '&:hover': {
                              transform: 'scale(1.1)',
                              transition: 'transform 0.2s',
                            },
                          }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(item.itemId)}
                          sx={{
                            '&:hover': {
                              transform: 'scale(1.1)',
                              transition: 'transform 0.2s',
                            },
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={isRTL ? 'عدد الصفوف:' : 'Rows per page:'}
            />
          </TableContainer>
        )}
      </Paper>

      <ItemDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        editItem={editingItem}
      />
    </Box>
  );
};

export default ItemsPage;